import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, ArrowLeft, ExternalLink, Instagram, MapPin, Search, UtensilsCrossed } from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

export default function FoodTrucks() {
  const [trucks, setTrucks] = useState([]);
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/food-trucks")
      .then(r => setTrucks(r.data))
      .finally(() => setLoading(false));
  }, []);

  const cuisines = useMemo(() => {
    const s = new Set();
    trucks.forEach(t => t.cuisine && s.add(t.cuisine));
    return [...s].sort();
  }, [trucks]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return trucks.filter(t => {
      if (cuisine && t.cuisine !== cuisine) return false;
      if (!needle) return true;
      return (
        (t.name || "").toLowerCase().includes(needle) ||
        (t.cuisine || "").toLowerCase().includes(needle) ||
        (t.handle || "").toLowerCase().includes(needle)
      );
    });
  }, [trucks, q, cuisine]);

  return (
    <div className="grain-bg min-h-screen">
      <div className="relative z-10 max-w-[900px] mx-auto px-4 pb-24">
        {/* Header */}
        <header className="pt-8 pb-4 flex items-baseline gap-3 border-b border-border">
          <Anchor className="text-[#1A3C5A]" size={22} strokeWidth={2} />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A3C5A]" data-testid="page-title">
            Southern Maine Food Trucks
          </h1>
          <Link to="/" className="ml-auto text-xs text-slate-500 font-display uppercase tracking-widest hover:text-[#1A3C5A] inline-flex items-center gap-1"
                data-testid="back-to-events">
            <ArrowLeft size={12} /> Events
          </Link>
        </header>

        {/* Subhead */}
        <div className="pt-4 pb-2 flex items-baseline justify-between">
          <div className="flex items-center gap-2 text-slate-600">
            <UtensilsCrossed size={16} className="text-[#B45309]" strokeWidth={2} />
            <span className="font-display text-sm">
              {loading ? "Loading…" : `${trucks.length} trucks we track from Portland to Ogunquit`}
            </span>
          </div>
          <div className="text-xs text-slate-500 font-display uppercase tracking-widest">
            Phase 2: live map
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 pt-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search truck, cuisine, or @handle…"
              className="pl-9 h-10 bg-white"
              data-testid="trucks-search"
            />
          </div>
          <Select value={cuisine || "__all"} onValueChange={(v) => setCuisine(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[200px] h-10 bg-white" data-testid="cuisine-filter">
              <SelectValue placeholder="All cuisines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All cuisines</SelectItem>
              {cuisines.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Count */}
        <div className="pt-4 pb-2 border-b border-border flex items-baseline justify-between">
          <div className="font-display text-lg font-semibold tracking-tight text-[#1E293B]" data-testid="truck-count">
            {loading ? "…" : `${filtered.length} truck${filtered.length === 1 ? "" : "s"}`}
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-display">
            Directory
          </div>
        </div>

        {/* Truck grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4" data-testid="trucks-grid">
          {filtered.length === 0 && !loading && (
            <div className="sm:col-span-2 py-16 text-center text-slate-500">
              <div className="font-display text-lg">No trucks match.</div>
              <div className="text-sm mt-1">Try clearing the search or cuisine filter.</div>
            </div>
          )}
          {filtered.map(t => (
            <div
              key={t.id}
              data-testid={`truck-card-${t.id}`}
              className="bg-white/70 border border-border rounded-md p-4 hover:border-[#1A3C5A] transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-[#1A3C5A] text-white flex items-center justify-center font-display font-bold text-sm">
                  {(t.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-[15px] leading-snug text-[#1E293B] truncate">
                    {t.name}
                  </div>
                  {t.cuisine && (
                    <Badge variant="outline" className="mt-1 border-[#B45309] text-[#B45309] text-[10px] font-semibold">
                      {t.cuisine}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[13px]">
                <a
                  href={t.ig_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-[#1A3C5A]"
                  data-testid={`truck-ig-${t.id}`}
                >
                  <Instagram size={13} strokeWidth={2} />
                  <span className="font-mono">@{t.handle}</span>
                </a>
                {t.website && (
                  <a
                    href={t.website.startsWith("http") ? t.website : `https://${t.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-[#1A3C5A]"
                    data-testid={`truck-web-${t.id}`}
                  >
                    <ExternalLink size={12} strokeWidth={2} /> Website
                  </a>
                )}
                {t.total_events > 0 && (
                  <span className="ml-auto text-xs text-slate-500">
                    {t.total_events} event{t.total_events === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Phase-2 hint */}
        <div className="mt-10 border border-dashed border-border rounded-md p-4 flex items-start gap-3 bg-white/40">
          <MapPin size={16} className="text-[#B45309] mt-0.5" strokeWidth={2} />
          <div className="text-sm text-slate-600">
            <div className="font-display font-semibold text-[#1E293B]">Live map coming soon</div>
            <div>
              We&apos;re pulling each truck&apos;s Instagram schedule daily. Once we have enough location signals,
              this page will show a map with today&apos;s truck locations pinned.
            </div>
          </div>
        </div>

        <footer className="pt-16 text-xs text-slate-500 font-display uppercase tracking-widest">
          Curated for the coast · <Link to="/" className="hover:text-[#1A3C5A]" data-testid="footer-events">Events</Link> ·{" "}
          <a href="/admin/login" className="hover:text-[#1A3C5A]" data-testid="admin-link">Admin</a>
        </footer>
      </div>
    </div>
  );
}
